import moment from "moment-timezone";
import type {FlattenMaps} from "mongoose";

import type {Languages} from "../../constant";
import {providerChannels} from "../../constant";
import {isBeta} from "../../constant/isBeta";
import type {
    Listing,
    Lock,
    LockDocument,
    ReservationCustom,
    ReservationGuest,
    ReservationPostDocument
} from "../../generated/models";
import type {ReservationDocument} from "../../models/user.account.listing.reservation";
import {Channel} from "../model/channel";
import type {Reservation} from "../model/reservation";

import {getListingTimezone} from "./utils.listing";

export const getFullName = <
    T extends {custom?: Pick<ReservationCustom, "firstName" | "lastName">} & Pick<
        Reservation,
        "airbnbFirstName" | "airbnbLastName"
    >
>(
    reservation: T
): string => {
    const {custom, airbnbFirstName, airbnbLastName} = reservation;
    let fullName = "";
    if (airbnbFirstName) {
        fullName = airbnbFirstName;
        if (airbnbLastName) {
            fullName += ` ${airbnbLastName}`;
        }
    }
    if (custom?.firstName) {
        fullName = custom.firstName;
        if (custom?.lastName) {
            fullName += ` ${custom.lastName}`;
        }
    }
    return fullName;
};

export const getFirstName = (
    reservation: Pick<ReservationDocument | Reservation, "custom" | "airbnbFirstName">
): string => {
    return reservation.custom?.firstName || reservation.airbnbFirstName || "";
};

export const getLastName = (
    reservation: Pick<ReservationDocument | Reservation, "custom" | "airbnbLastName">
): string => {
    return reservation.custom?.lastName || reservation.airbnbLastName || "";
};

export const isExternalReservation = (
    reservation: Pick<ReservationDocument | Reservation, "source">
): boolean => {
    return [
        Channel.Airbnb,
        Channel.Booking,
        Channel.HomeAway,
        Channel.Houfy,
        Channel.ChannelConnector
    ].includes(reservation.source as Channel);
};

export type ChannelReservation<
    T extends {source: Reservation["source"]; accountID?: Reservation["accountID"]}
> = Omit<T, "accountID" | "source"> & {
    source: Exclude<T["source"], "internal" | "direct">;
    accountID: RequiredKeys<T, "accountID">;
};

export const isChannelReservation = <
    // this can accept this form of reservation type
    T extends {source: Reservation["source"]; accountID?: Reservation["accountID"]}
>(
    reservation: any
): reservation is ChannelReservation<T> => providerChannels.includes(reservation?.source);

export const isInternalReservation = (
    channel: Reservation["source"] | undefined
): channel is "internal" => channel === "internal";

export const isDirectReservation = (
    channel: Reservation["source"] | undefined
): channel is "direct" => channel === "direct";

export const getEmail = (
    reservation: Pick<ReservationDocument | Reservation, "custom" | "airbnbEmail">
): string => {
    return reservation.custom?.email || reservation.airbnbEmail || "";
};

export const getReservationEmail = (
    reservation: Pick<ReservationDocument | Reservation, "_id">
) => {
    return `${reservation._id.toString()}@${isBeta ? "beta." : ""}mail.hosttools.com`;
};

export const getPhone = (
    reservation: Pick<ReservationDocument | Reservation, "custom" | "airbnbPhone">
): string => {
    return reservation.custom?.phone || reservation.airbnbPhone || "";
};

export const getLockCode = (
    reservation: Pick<Reservation, "_id" | "custom" | "airbnbPhone" | "createdAt">,
    pinLength: number,
    lock?: Lock | FlattenMaps<LockDocument>
): string => {
    let phoneNumber = getPhone(reservation);

    if (!phoneNumber && reservation.createdAt) {
        // if the reservation is missing a phone number, use the date it was created to generate a pin
        phoneNumber = new Date(reservation.createdAt).getTime().toString();
    } else if (!phoneNumber) {
        // if the reservation is missing a phone number, use the _id to generate a pin
        phoneNumber = reservation._id.toString();
    }
    phoneNumber = phoneNumber.replace(/[^0-9]/g, "");
    // Make sure there is at least X numbers in the phone number
    if (phoneNumber.length < pinLength) {
        return "";
    }
    let pin = phoneNumber.substring(phoneNumber.length - pinLength);

    // rules of `nuki` lock
    // https://docs.seam.co/latest/device-guides/get-started-with-nuki-locks#5-setting-access-code-on-nuki-lock
    if (lock?.manufacturer === "nuki") {
        pin = pin.replace(/0/g, "1");
        pin = pin.replace(/^12/, "13");
    }

    return pin;
};

export const getNotes = (
    reservation: Pick<ReservationDocument | Reservation, "custom">
): string => {
    return reservation.custom?.notes || "";
};

export const getPreferLanguage = (
    reservation: Pick<ReservationDocument | Reservation, "custom" | "airbnbPreferredLocale">
): Languages | "default" => {
    return reservation.custom?.preferredLocale || reservation.airbnbPreferredLocale || "default";
};

export const getCheckInTime = (
    reservation: {custom?: Pick<ReservationCustom, "checkInTime">},
    listing: Pick<Listing, "airbnbCheckInTime">
): number | undefined => {
    return reservation.custom?.checkInTime ?? listing.airbnbCheckInTime;
};

export const getCheckOutTime = (
    reservation: {custom?: Pick<ReservationCustom, "checkOutTime">},
    listing: Pick<Listing, "airbnbCheckOutTime">
): number | undefined => {
    return reservation.custom?.checkOutTime ?? listing.airbnbCheckOutTime;
};

export function getCheckInDate(
    reservation: Pick<Reservation, "custom" | "startDate">,
    listing: {
        airbnbCheckInTime?: Listing["airbnbCheckInTime"];
        timeZone?: Listing["timeZone"];
    }
) {
    const {startDate} = reservation;
    const checkInTime = getCheckInTime(reservation, listing);
    const checkInDate = moment
        .tz(startDate, getListingTimezone(listing))
        .startOf("day")
        // Date is sent as UTC so remove day light savings
        // .add(moment.tz(startDate, timeZone).isDST() ? 1 : 0, "hours")
        .add(checkInTime, "hour")
        .toDate();

    return checkInDate;
}

export function getCheckOutDate(
    reservation: Pick<Reservation, "custom" | "endDate">,
    listing: {
        airbnbCheckOutTime?: Listing["airbnbCheckOutTime"];
        timeZone?: Listing["timeZone"];
    }
) {
    const {endDate} = reservation;
    const checkOutTime = getCheckOutTime(reservation, listing);
    const checkOutDate = moment
        .tz(endDate, getListingTimezone(listing))
        .startOf("day")
        // Date is sent as UTC so remove day light savings
        // .add(moment.tz(endDate, timeZone).isDST() ? 1 : 0, "hours")
        .add(checkOutTime, "hour")
        .toDate();

    return checkOutDate;
}

export const getGuests = (reservation: Pick<Reservation, "guests">): number => {
    return (
        (reservation.guests?.adults || 0) +
        (reservation.guests?.children || 0) +
        (reservation.guests?.infants || 0)
    );
};

export const getAdults = (reservation: Pick<Reservation, "guests" | "custom">): number => {
    const {guests, custom} = reservation;
    if (custom?.guests) {
        return custom.guests.adults || 0;
    }
    return guests?.adults || 0;
};

export const getChildren = (reservation: Pick<Reservation, "guests" | "custom">): number => {
    const {guests, custom} = reservation;
    if (custom?.guests) {
        return custom.guests.children || 0;
    }
    return guests?.children || 0;
};

export const getInfants = (reservation: Pick<Reservation, "guests" | "custom">): number => {
    const {guests, custom} = reservation;
    if (custom?.guests) {
        return custom.guests.infants || 0;
    }
    return guests?.infants || 0;
};

export const getPets = (reservation: Pick<Reservation, "guests" | "custom">): number => {
    const {guests, custom} = reservation;
    if (custom?.guests) {
        return custom.guests.pets || 0;
    }
    return guests?.pets || 0;
};

export const getNumberOfGuests = (reservation: Pick<Reservation, "guests" | "custom">): number => {
    return getAdults(reservation) + getChildren(reservation) + getInfants(reservation);
};

export const getNights = (reservation: Pick<Reservation, "airbnbNights">): number | undefined => {
    return reservation.airbnbNights;
};

export const getThumbnail = (
    reservation: Pick<Reservation, "airbnbThumbnailUrl">
): string | undefined => {
    return reservation.airbnbThumbnailUrl;
};

export const getGuestPrice = (reservation: Pick<Reservation, "guestPrice">) => {
    return reservation.guestPrice ? reservation.guestPrice.toFixed(2) : "";
};

export const getHostPayout = (reservation: Pick<Reservation, "hostPayout">) => {
    return reservation.hostPayout ? reservation.hostPayout.toFixed(2) : "";
};

export const getGuestPriceWithCurrency = (
    reservation: Pick<Reservation, "guestPrice">,
    currencySymbol: string
) => {
    return reservation.guestPrice ? currencySymbol + getGuestPrice(reservation) : "";
};

export const getHostPayoutWithCurrency = (
    reservation: Pick<Reservation, "hostPayout">,
    currencySymbol: string
) => {
    return reservation.hostPayout ? currencySymbol + getHostPayout(reservation) : "";
};

export const sanitize = <T extends {startDate?: string | Date; endDate?: string | Date}>(
    reservation: T
) => {
    const {startDate, endDate} = reservation;
    if (!startDate || !endDate) {
        return reservation;
    }

    if (new Date(endDate) < new Date(startDate)) {
        reservation.startDate = endDate;
        reservation.endDate = startDate;
    }
    return reservation;
};

export const getExternalUrl = (
    reservation: Pick<
        Reservation,
        | "source"
        | "airbnbConfirmationCode"
        | "airbnbListingID"
        | "airbnbThreadID"
        | "houfyReservationID"
    >
) => {
    switch (reservation.source) {
        case Channel.Airbnb:
            return `https://www.airbnb.com/messaging/qt_for_reservation/${reservation.airbnbConfirmationCode}`;
        case Channel.Booking:
            return "https://admin.booking.com/";
        case Channel.HomeAway:
            return `https://www.homeaway.com/rm/${reservation.airbnbListingID}/conversation/${reservation.airbnbThreadID}`;
        case Channel.Houfy:
            return `https://houfy.com/reservation/${reservation.houfyReservationID}`;
        default:
            return "";
    }
};

export const filterInvalidReservationPost = (items: ReservationPostDocument[]) =>
    items.filter(elem => elem.message || elem.attachments?.length);

export const formatListingCurrency = (value: number, currency: string = "USD") => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(value);
};

/**
 * Cleans a guests object.
 * - Converts string values to numbers
 * - Removes values that are 0, NaN, or invalid
 *
 * @param guests - The guests object with values that can be numbers or strings
 * @returns A cleaned guests object with only valid number values > 0, or undefined if no valid guests
 */
export function cleanGuests(guests?: Partial<ReservationGuest>): ReservationGuest | undefined {
    if (!guests) {
        return undefined;
    }

    const cleanedGuests: Partial<ReservationGuest> = {};

    // Process each guest type: convert to number, validate, and only keep if > 0
    if (guests.adults !== undefined && guests.adults !== null) {
        const adultsNum = Number(guests.adults);
        if (!Number.isNaN(adultsNum) && adultsNum > 0) {
            cleanedGuests.adults = adultsNum;
        }
    }

    if (guests.children !== undefined && guests.children !== null) {
        const childrenNum = Number(guests.children);
        if (!Number.isNaN(childrenNum) && childrenNum > 0) {
            cleanedGuests.children = childrenNum;
        }
    }

    if (guests.infants !== undefined && guests.infants !== null) {
        const infantsNum = Number(guests.infants);
        if (!Number.isNaN(infantsNum) && infantsNum > 0) {
            cleanedGuests.infants = infantsNum;
        }
    }

    if (guests.pets !== undefined && guests.pets !== null) {
        const petsNum = Number(guests.pets);
        if (!Number.isNaN(petsNum) && petsNum > 0) {
            cleanedGuests.pets = petsNum;
        }
    }

    // If no valid guests were added, return undefined
    if (Object.keys(cleanedGuests).length === 0) {
        return undefined;
    }

    return cleanedGuests as ReservationGuest;
}
